/* ============================================================
   NOOK Film, cinema layout (sections/nook-film.liquid).
   Loaded deferred, only when a band uses layout = cinema.

   Teaser: the MP4 is attached only when the stage comes within
   200 px of the viewport (never under reduced motion or Save-Data),
   plays at >= 50 % visible, pauses when it leaves.
   Full film: a native <dialog>; the YouTube iframe and the IFrame
   API are fetched on the click only. `ended` shows the end card.
   Tracking goes through NookTrack with the dataLayer fallback.

   Every listener, observer and timer a band creates is registered
   with the band, so `shopify:section:unload` disposes it completely
   and `shopify:section:load` can re-init without duplicates.
   ============================================================ */
(function () {
  'use strict';

  if (window.NookFilmCinema) {
    window.NookFilmCinema.scan(document);
    return;
  }

  var MOBILE = '(max-width: 767px)';
  var QUARTILES = [25, 50, 75];
  var YT_API_SRC = 'https://www.youtube.com/iframe_api';
  var bands = [];

  function matches(query) {
    return !!(window.matchMedia && window.matchMedia(query).matches);
  }

  function track(event, props) {
    if (window.NookTrack) {
      window.NookTrack.push(event, props);
      return;
    }
    if (typeof window.dataLayer === 'undefined') return;
    var payload = { event: event };
    Object.keys(props).forEach(function (k) { payload[k] = props[k]; });
    window.dataLayer.push(payload);
  }

  function focusSafely(el) {
    if (el && el.isConnected && typeof el.focus === 'function') el.focus({ preventScroll: true });
  }

  /* ── YouTube IFrame API: one shared promise for the whole page ──
     Reuses an API that is already loaded, or being loaded by another
     script, and chains any existing onYouTubeIframeAPIReady. */
  var ytPromise = null;

  function ytReady() {
    return !!(window.YT && typeof window.YT.Player === 'function');
  }

  function loadYT() {
    if (ytPromise) return ytPromise;
    ytPromise = new Promise(function (resolve, reject) {
      if (ytReady()) {
        resolve(window.YT);
        return;
      }
      var poll = null;
      var settled = false;
      function fail(err) {
        if (settled) return;
        settled = true;
        if (poll) clearInterval(poll);
        ytPromise = null;
        reject(err);
      }
      function done() {
        if (settled || !ytReady()) return;
        settled = true;
        if (poll) clearInterval(poll);
        resolve(window.YT);
      }

      var previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        if (typeof previous === 'function') previous.apply(this, arguments);
        done();
      };
      // The API's own loader stub exposes YT.ready while it is in flight.
      if (window.YT && typeof window.YT.ready === 'function') window.YT.ready(done);

      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        var tag = document.createElement('script');
        tag.src = YT_API_SRC;
        tag.async = true;
        tag.onerror = function () {
          if (tag.parentNode) tag.parentNode.removeChild(tag);
          fail(new Error('YouTube IFrame API failed to load'));
        };
        document.head.appendChild(tag);
      }

      // Belt and braces: another script may replace our callback without
      // chaining it. Stop asking after 15 s.
      var tries = 0;
      poll = setInterval(function () {
        tries += 1;
        if (ytReady()) {
          done();
        } else if (tries > 60) {
          // A later click starts a fresh load instead of waiting forever.
          var stale = document.querySelector('script[src="' + YT_API_SRC + '"]');
          if (stale && stale.parentNode && !ytReady()) stale.parentNode.removeChild(stale);
          fail(new Error('YouTube IFrame API timed out'));
        }
      }, 250);
    });
    return ytPromise;
  }

  /* ── One band ─────────────────────────────────────────────── */
  function initBand(root) {
    for (var i = 0; i < bands.length; i++) {
      if (bands[i].root === root) return;
    }

    var videoId = root.getAttribute('data-video-id') || '';
    var stage = root.querySelector('[data-film-stage]');
    var video = root.querySelector('.nook-film__teaser');
    var toggle = root.querySelector('[data-film-toggle]');
    var pill = root.querySelector('.nook-film__pill');
    var fill = root.querySelector('.nook-film__progress-fill');
    var dialog = root.querySelector('[data-film-dialog]');
    var mount = root.querySelector('[data-film-mount]');
    var endCard = root.querySelector('[data-film-end]');

    var listeners = [];
    var observers = [];
    var timers = { view: null };
    var firedQuartiles = {};
    var session = null;
    var active = false;
    var opener = null;
    var teaser = { attached: false, blocked: false, visible: false, userPaused: false, started: false };

    function on(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      listeners.push([target, type, fn, opts]);
    }

    function observe(io, el) {
      io.observe(el);
      observers.push(io);
      return io;
    }

    function payload(extra) {
      var p = { video_id: videoId };
      if (extra) Object.keys(extra).forEach(function (k) { p[k] = extra[k]; });
      return p;
    }

    function isOpen() {
      return !!(dialog && (dialog.open || dialog.hasAttribute('open')));
    }

    /* Band view: >= 50 % of the band visible for a continuous 1 s, once.
       A band taller than the viewport counts once it covers >= 50 % of
       the viewport. */
    function clearViewTimer() {
      if (timers.view) {
        clearTimeout(timers.view);
        timers.view = null;
      }
    }

    if (typeof window.IntersectionObserver === 'function') {
      var steps = [0, 0.5];
      for (var s = 1; s < 20; s++) steps.push(s / 20);
      var viewDone = false;
      var viewIo = new IntersectionObserver(function (entries) {
        if (viewDone) return;
        var e = entries[entries.length - 1];
        var vh = (e.rootBounds && e.rootBounds.height) || window.innerHeight || 0;
        var tall = e.boundingClientRect.height > vh;
        var enough = e.isIntersecting &&
          (e.intersectionRatio >= 0.5 || (tall && e.intersectionRect.height >= vh * 0.5));
        if (enough && !document.hidden && !timers.view) {
          timers.view = setTimeout(function () {
            timers.view = null;
            viewDone = true;
            viewIo.disconnect();
            track('nook_film_band_view', payload());
          }, 1000);
        } else if (!enough) {
          clearViewTimer();
        }
      }, { threshold: steps });
      observe(viewIo, root);
      on(document, 'visibilitychange', function () {
        if (document.hidden) clearViewTimer();
      });
    }

    /* ── Teaser ── */
    function tryPlay() {
      if (!teaser.attached || teaser.blocked || teaser.userPaused || !teaser.visible || isOpen()) return;
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function (err) {
          // Autoplay refused (Low Power Mode, browser policy): the poster
          // stays up and we stop asking. An AbortError (a pause() racing
          // the play() promise) is harmless and the next play retries.
          if (err && err.name === 'NotAllowedError') teaser.blocked = true;
        });
      }
    }

    function setToggle() {
      if (!toggle) return;
      var paused = teaser.userPaused;
      toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
      toggle.setAttribute('aria-label', toggle.getAttribute(paused ? 'data-label-play' : 'data-label-pause') || '');
      toggle.classList.toggle('is-paused', paused);
    }

    function attachTeaser() {
      if (teaser.attached) return;
      var d = video.getAttribute('data-src-desktop');
      var m = video.getAttribute('data-src-mobile');
      var src = matches(MOBILE) ? (m || d) : (d || m);
      if (!src) return;
      teaser.attached = true;
      var still = root.querySelector('.nook-film__still-img');
      if (still && still.currentSrc) video.poster = still.currentSrc;
      var source = document.createElement('source');
      source.src = src;
      source.type = 'video/mp4';
      video.appendChild(source);
      on(video, 'playing', function () {
        root.classList.add('is-teaser-on');
        if (toggle) toggle.hidden = false;
        if (!teaser.started) {
          teaser.started = true;
          track('nook_film_teaser_start', payload());
        }
      });
      on(video, 'timeupdate', function () {
        if (!fill || !video.duration) return;
        fill.style.setProperty('--p', (video.currentTime / video.duration).toFixed(4));
      });
      video.load();
      tryPlay();
    }

    var reduce = matches('(prefers-reduced-motion: reduce)');
    var saveData = !!(navigator.connection && navigator.connection.saveData);
    if (video && stage && !reduce && !saveData && typeof window.IntersectionObserver === 'function') {
      var arm = function () {
        var nearIo = new IntersectionObserver(function (entries) {
          if (entries[entries.length - 1].isIntersecting) {
            nearIo.disconnect();
            attachTeaser();
          }
        }, { rootMargin: '200px 0px' });
        var seenIo = new IntersectionObserver(function (entries) {
          teaser.visible = entries[entries.length - 1].intersectionRatio >= 0.5;
          if (teaser.visible) {
            tryPlay();
          } else if (teaser.attached) {
            video.pause();
          }
        }, { threshold: [0, 0.5, 1] });
        observe(nearIo, stage);
        observe(seenIo, stage);
      };
      if (document.readyState === 'complete') {
        arm();
      } else {
        on(window, 'load', arm, { once: true });
      }
    }

    if (toggle) {
      on(toggle, 'click', function (e) {
        e.stopPropagation();
        teaser.userPaused = !teaser.userPaused;
        if (teaser.userPaused) {
          video.pause();
          track('nook_film_teaser_pause', payload());
        } else {
          tryPlay();
        }
        setToggle();
      });
    }

    /* ── Full film ── */
    function showEnd() {
      if (!endCard) return;
      endCard.hidden = false;
      focusSafely(endCard.querySelector('a, button'));
    }

    function hideEnd() {
      if (endCard) endCard.hidden = true;
    }

    function stopPoll(sess) {
      if (sess.poll) {
        clearInterval(sess.poll);
        sess.poll = null;
      }
    }

    function fireQuartile(q) {
      if (firedQuartiles[q]) return;
      firedQuartiles[q] = true;
      track('nook_film_full_progress', payload({ pct: q }));
    }

    // Quartiles fire once per page, and only for the part actually watched:
    // opening a chapter at 1:10 does not count 25 % and 50 % as watched.
    function startPoll(sess) {
      stopPoll(sess);
      sess.poll = setInterval(function () {
        var p = sess.player;
        if (!p || typeof p.getDuration !== 'function') return;
        var dur = p.getDuration();
        if (!dur) return;
        if (sess.fromPct === null) sess.fromPct = (sess.startS / dur) * 100;
        var pct = (p.getCurrentTime() / dur) * 100;
        QUARTILES.forEach(function (q) {
          if (pct >= q && q > sess.fromPct) fireQuartile(q);
        });
      }, 1000);
    }

    function onState(sess, state) {
      if (session !== sess) return;
      var S = window.YT.PlayerState;
      if (state === S.PLAYING) {
        hideEnd();
        if (!sess.started) {
          sess.started = true;
          track('nook_film_full_start', payload());
        }
        startPoll(sess);
        return;
      }
      stopPoll(sess);
      if (state === S.ENDED) {
        fireQuartile(100);
        showEnd();
      }
    }

    function buildPlayer(startS) {
      var start = Math.max(0, Math.floor(startS || 0));
      var inline = matches(MOBILE) ? 0 : 1;
      var iframe = document.createElement('iframe');
      iframe.className = 'nook-film__iframe';
      iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(videoId) +
        '?autoplay=1&rel=0&modestbranding=1&enablejsapi=1&start=' + start +
        '&playsinline=' + inline + '&origin=' + encodeURIComponent(window.location.origin);
      iframe.title = dialog.getAttribute('data-iframe-title') || '';
      iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
      iframe.setAttribute('allowfullscreen', '');
      mount.appendChild(iframe);

      var sess = { iframe: iframe, player: null, startS: start, fromPct: null, started: false, poll: null };
      session = sess;
      loadYT().then(function (YT) {
        if (session !== sess) return;
        sess.player = new YT.Player(iframe, {
          events: {
            onStateChange: function (ev) { onState(sess, ev.data); }
          }
        });
      }).catch(function (err) {
        // The iframe still plays; only the tracking and the end card are lost.
        sess.apiError = err && err.message;
      });
    }

    function open(trigger, startS, from) {
      if (!dialog || !mount || !videoId || isOpen()) return;
      active = true;
      opener = from || null;
      track('nook_film_open', payload({ trigger: trigger }));
      if (teaser.attached) video.pause();
      hideEnd();
      document.body.classList.add('nook-film-open');
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      buildPlayer(startS);
    }

    // Idempotent: close() tears down at once; the dialog's own `close` event
    // (queued, and the only signal for Esc) then finds nothing to do, and
    // never touches a film that was reopened in between.
    function teardown(silent) {
      if (!active) return;
      active = false;
      var at = null;
      if (session) {
        var p = session.player;
        if (p && typeof p.getCurrentTime === 'function') at = Math.round(p.getCurrentTime() || 0);
        stopPoll(session);
        if (p && typeof p.destroy === 'function') p.destroy();
        session = null;
      }
      while (mount.firstChild) mount.removeChild(mount.firstChild);
      hideEnd();
      document.body.classList.remove('nook-film-open');
      if (!silent) track('nook_film_close', payload({ at_s: at }));
      var back = opener;
      opener = null;
      focusSafely(back);
      if (!silent) tryPlay();
    }

    function close(silent) {
      if (isOpen()) {
        if (typeof dialog.close === 'function') {
          dialog.close();
        } else {
          dialog.removeAttribute('open');
        }
      }
      teardown(silent);
    }

    // The element an in-page link (#id) points at, or null for any other URL.
    function hashTarget(link) {
      var href = link.getAttribute('href') || '';
      if (href.charAt(0) !== '#' || href.length < 2) return null;
      var id;
      try {
        id = decodeURIComponent(href.slice(1));
      } catch (err) {
        id = href.slice(1);
      }
      return document.getElementById(id);
    }

    function isPlainClick(e, link) {
      return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey &&
        (link.getAttribute('target') || '_self') === '_self';
    }

    if (dialog && mount) {
      on(dialog, 'close', function () {
        if (!isOpen()) teardown();
      });
      on(dialog, 'click', function (e) {
        if (e.target === dialog || e.target.hasAttribute('data-film-backdrop')) {
          close();
          return;
        }
        if (e.target.closest('[data-film-close]')) {
          close();
          return;
        }
        if (e.target.closest('[data-film-replay]')) {
          var p = session && session.player;
          if (p && typeof p.seekTo === 'function') {
            session.fromPct = 0;
            hideEnd();
            p.seekTo(0, true);
            p.playVideo();
          }
          return;
        }
        var cta = e.target.closest('[data-film-cta]');
        if (!cta) return;
        track('nook_film_end_cta', payload({ cta: cta.getAttribute('data-film-cta') }));
        if (!isPlainClick(e, cta)) return;
        var target = hashTarget(cta);
        if (!target) {
          // A page or an external URL: close the film and let the browser
          // follow the link (never preventDefault a real navigation).
          close();
          return;
        }
        // An in-page anchor (for example #close, the dossier form): the
        // browser will not re-scroll when the hash is already the same, so
        // close, release the body lock, then scroll the target ourselves.
        e.preventDefault();
        close();
        var hash = cta.getAttribute('href');
        if (window.location.hash !== hash && window.history && typeof window.history.pushState === 'function') {
          window.history.pushState(null, '', hash);
        }
        window.requestAnimationFrame(function () {
          target.scrollIntoView({ behavior: matches('(prefers-reduced-motion: reduce)') ? 'auto' : 'smooth', block: 'start' });
        });
      });
      if (typeof dialog.showModal !== 'function') {
        on(document, 'keydown', function (e) {
          if (e.key === 'Escape' && isOpen()) close();
        });
      }
    }

    if (stage) {
      on(stage, 'click', function (e) {
        if (e.target.closest('[data-film-toggle]')) return;
        var fromPill = e.target.closest('[data-film-open]');
        // The stage itself is not focusable: the play pill stands in as the
        // element focus returns to.
        open(fromPill ? 'play_button' : 'stage', 0, pill || root.querySelector('.nook-film__ch'));
      });
    }

    on(root, 'click', function (e) {
      var ch = e.target.closest('[data-film-chapter]');
      if (!ch || ch.disabled) return;
      open('chapter:' + (ch.getAttribute('data-film-label') || ''), parseFloat(ch.getAttribute('data-film-chapter')) || 0, ch);
    });

    // Openers elsewhere on the page: data-nook-film-open="<this band's id>".
    on(document, 'click', function (e) {
      var el = e.target.closest ? e.target.closest('[data-nook-film-open]') : null;
      if (!el || !root.id || el.getAttribute('data-nook-film-open') !== root.id) return;
      e.preventDefault();
      open('close_link', 0, el);
    });

    function dispose() {
      close(true);
      clearViewTimer();
      if (video && teaser.attached) video.pause();
      observers.forEach(function (io) { io.disconnect(); });
      listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
      observers = [];
      listeners = [];
      root.classList.remove('is-teaser-on');
    }

    bands.push({ root: root, dispose: dispose });
  }

  function scan(scope) {
    var el = scope || document;
    var roots = Array.prototype.slice.call(el.querySelectorAll('[data-nook-film-cinema]'));
    if (el.matches && el.matches('[data-nook-film-cinema]')) roots.unshift(el);
    roots.forEach(initBand);
  }

  function disposeWithin(scope) {
    bands = bands.filter(function (b) {
      if (scope === b.root || scope.contains(b.root) || !b.root.isConnected) {
        b.dispose();
        return false;
      }
      return true;
    });
  }

  window.NookFilmCinema = { scan: scan, dispose: disposeWithin };
  scan(document);
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { disposeWithin(e.target); });
})();
